import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [supportCode, setSupportCode] = useState('');
  const [debugDetails, setDebugDetails] = useState('');
  const { login, rememberMe, setRememberMe } = useAuth();
  const navigate = useNavigate();
  const isDev = import.meta.env?.DEV;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSupportCode('');
    setDebugDetails('');
    setLoading(true);

    const label = `login-${Date.now()}`;
    console.time(label);
    console.log('[Login] starting request');

    try {
      await login(email, password, rememberMe);
      console.log('[Login] request completed');
      navigate('/');
    } catch (err) {
      console.error('[Login] request failed:', err);
      const fallbackCode = err?.code || `auth-${Date.now().toString(36)}`;
      setSupportCode(String(fallbackCode).toUpperCase());
      setDebugDetails(err?.rawMessage || err?.message || '');

      // Parse error message for user-friendly display
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (err.message) {
        if (err.message.includes('Invalid credentials') || err.message.includes('401')) {
          errorMessage = '⚠️ Invalid email or password. Please try again.';
        } else if (err.message.includes('400')) {
          errorMessage = '⚠️ Please enter both email and password.';
        } else if (err.message.includes('500') || err.message.includes('502')) {
          errorMessage = '⚠️ Server error. Please try again in a moment.';
        } else if (err.message.includes('504')) {
          errorMessage = '⚠️ The login service is taking too long to respond. Please retry in a moment.';
        } else if (err.message.includes('network')) {
          errorMessage = '⚠️ We could not reach the login service. Check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      console.timeEnd(label);
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-purple-500 items-center justify-center font-bold text-3xl mb-4">
            AI
          </div>
          <h1 className="text-3xl font-bold">Welcome back to AIIA</h1>
          <p className="text-slate-400 mt-2">Login to discover AI-verified content</p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-slate-900/60 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-2xl bg-red-500/10 border border-red-500/40 text-red-200 px-4 py-3 text-sm">
                {error}
                {supportCode && (
                  <p className="mt-2 text-xs text-red-200/80">
                    Reference: <span className="font-mono">{supportCode}</span>
                  </p>
                )}
                {debugDetails && isDev && (
                  <details className="mt-2 text-xs text-red-200/80">
                    <summary className="cursor-pointer">Debug details</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-words opacity-80">{debugDetails}</pre>
                  </details>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
                required
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="inline-flex items-center gap-2 select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-white/30 bg-white/5 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-slate-300">Keep me logged in</span>
              </label>

              <button
                type="button"
                onClick={() => navigate('/reset-password')}
                className="text-blue-400 hover:text-blue-300"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 font-semibold text-white hover:shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-400">
            Don&apos;t have an account?{' '}
            <button
              onClick={() => navigate('/signup')}
              className="text-blue-400 hover:text-blue-300 font-semibold"
            >
              Sign up
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
